import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Badge,
} from "react-bootstrap";
import { Send, Paperclip, User, Bot } from "lucide-react";
import { sendChatMessage } from "../api/chatbot";
import Spinner from "react-bootstrap/Spinner";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
function ChatApp() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const location = useLocation();
  const service = location.state?.selectedService || null;

  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachedFiles.length === 0) return;

    const newMessage = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
      timestamp: new Date(),
      attachments: attachedFiles.length > 0 ? [...attachedFiles] : undefined,
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputValue("");
    setAttachedFiles([]);
    setLoading(true);

    try {
      const res = await sendChatMessage(
        service,
        newMessage.content,
        attachedFiles
      );
      const botMessage = {
        id: (Date.now() + 1).toString(),
        content: res?.data || "No response received",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 2).toString(),
          content: `Error: ${err.message}`,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    }

    setLoading(false);
  };

  const handleFileAttachment = (event) => {
    const files = event.target.files;
    if (files) {
      setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const removeAttachment = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (text) => {
    setInputValue(text);
  };

  const BOT_CONFIG = {
    chatgpt: {
      heading: "💡 Brainstorm, Write & Create with ChatGPT",
      subtext:
        "Create, brainstorm, and refine your ideas with secure AI writing support.",
      quickOptions: [
        "Draft a story idea",
        "Rewrite & improve my text",
        "Explore creative concepts",
        "Plan a project outline",
        "Solve a problem step-by-step",
      ],
    },
    perplexity: {
      heading: "🔍 Research with Clarity using Perplexity",
      subtext:
        "Get clear, fact-based answers with trusted sources — privately and fast.",
      quickOptions: [
        "Find latest scientific research",
        "Summarize an article",
        "Verify a fact with sources",
        "Explain a complex topic",
      ],
    },
    bedrock: {
      heading: "🛡️ Private, Enterprise-Grade AI with Bedrock",
      subtext: "Enterprise-grade AI for secure problem solving and analysis.",
      quickOptions: [
        "Analyze my draft securely",
        "Generate a structured report",
        "Summarize complex data",
        "Help me plan a strategy",
        "Expand on a technical concept",
      ],
    },
  };

  const botConfig = BOT_CONFIG[service?.toLowerCase()] || BOT_CONFIG.chatgpt;

  return (
    <>
      <div className="chat-container">
        <Container fluid className="h-100 d-flex flex-column p-0">
          {/* Main Chat Area */}
          <div className="chat-main flex-grow-1 d-flex align-items-center justify-content-center">
            {messages.length === 0 ? (
              <Container>
                <Row className="justify-content-center">
                  <Col lg={8} xl={6}>
                    <div className="welcome-screen text-center">
                      <h1 className="welcome-title mb-4">
                        {botConfig.heading}
                      </h1>
                      <p className="welcome-subtitle mb-5">
                        {botConfig.subtext}
                      </p>

                      <div className="quick-actions mb-5">
                        {botConfig.quickOptions.map((text, idx) => (
                          <Button
                            key={idx}
                            variant="outline-secondary"
                            size="sm"
                            className="quick-action-btn me-2 mb-2"
                            onClick={() => handleQuickAction(text)}
                          >
                            {text}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            ) : (
              <Container fluid className="messages-container h-100">
                <Row className="justify-content-center h-100">
                  <Col lg={10} xl={8} className="h-100">
                    <div className="messages-list h-100 overflow-auto py-4">
                      {messages.map((message) => (
                        <Row
                          key={message.id}
                          className={`message-row mb-4 ${
                            message.sender === "user"
                              ? "justify-content-end"
                              : "justify-content-start"
                          }`}
                        >
                          <Col xs={12} md={10} lg={8}>
                            <div
                              className={`d-flex gap-3 ${
                                message.sender === "user"
                                  ? "flex-row-reverse"
                                  : "flex-row"
                              }`}
                            >
                              {/* <div
                                className={`avatar ${
                                  message.sender === "user"
                                    ? "avatar-user"
                                    : "avatar-bot"
                                }`}
                              >
                                {message.sender === "bot" ? (
                                  <Bot size={16} />
                                ) : (
                                  <User size={16} />
                                )}
                              </div> */}
                              <Card
                                className={`message-card ${
                                  message.sender === "user"
                                    ? "message-user"
                                    : "message-bot"
                                }`}
                              >
                                <Card.Body className="p-3">
                                  {/* {message.isHTML ? (
                                    <div
                                      className="message-text mb-2"
                                      dangerouslySetInnerHTML={{
                                        __html: message.content,
                                      }}
                                    />
                                  ) : (
                                    <p className="message-text mb-2">
                                      {message.content}
                                    </p>
                                  )} */}

                                  <ReactMarkdown
                                    children={message.content}
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      code({
                                        node,
                                        inline,
                                        className,
                                        children,
                                        ...props
                                      }) {
                                        const match = /language-(\w+)/.exec(
                                          className || ""
                                        );
                                        return !inline && match ? (
                                          <SyntaxHighlighter
                                            style={oneDark}
                                            language={match[1]}
                                            PreTag="div"
                                            {...props}
                                          >
                                            {String(children).replace(
                                              /\n$/,
                                              ""
                                            )}
                                          </SyntaxHighlighter>
                                        ) : (
                                          <code
                                            className={className}
                                            {...props}
                                          >
                                            {children}
                                          </code>
                                        );
                                      },
                                    }}
                                  />

                                  {message.attachments &&
                                    message.attachments.length > 0 && (
                                      <div className="message-attachments">
                                        {message.attachments.map(
                                          (file, index) => (
                                            <Badge
                                              key={index}
                                              bg="dark"
                                              className="attachment-badge me-1 mb-1"
                                            >
                                              📎 {file.name}
                                            </Badge>
                                          )
                                        )}
                                      </div>
                                    )}
                                  {/* <small className="message-timestamp text-muted">
                                    {message.timestamp.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </small> */}
                                </Card.Body>
                              </Card>
                            </div>
                          </Col>
                        </Row>
                      ))}
                      {loading && (
                        <div className="text-center text-muted mt-2">
                          <div className="loader_global">
                            <Spinner animation="grow" /> Thinking
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Container>
            )}
          </div>

          {/* Input Area */}
          <div className="input-area">
            <Container fluid>
              <Row className="justify-content-center">
                <Col lg={10} xl={8}>
                  {attachedFiles.length > 0 && (
                    <div className="attached-files mb-3">
                      {attachedFiles.map((file, index) => (
                        <Badge
                          key={index}
                          bg="secondary"
                          className="attached-file me-2 mb-2 d-inline-flex align-items-center"
                        >
                          <Paperclip size={14} className="me-1" />
                          <span>{file.name}</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="remove-attachment p-0 ms-2"
                            onClick={() => removeAttachment(index)}
                          >
                            ×
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Card className="input-card">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-end gap-3">
                        <Button
                          variant="link"
                          className="attachment-btn p-2"
                          onClick={() => fileInputRef.current?.click()}
                          title="Attach files"
                        >
                          <Paperclip size={20} />
                        </Button>
                        <Form.Control
                          as="textarea"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={handleKeyPress}
                          placeholder="Type your idea..."
                          className="text-input flex-grow-1"
                          rows={1}
                          style={{
                            resize: "none",
                            minHeight: "40px",
                            maxHeight: "120px",
                          }}
                          onInput={(e) => {
                            const target = e.target;
                            target.style.height = "auto";
                            target.style.height = target.scrollHeight + "px";
                          }}
                        />
                        <Button
                          variant="primary"
                          className="send-btn"
                          onClick={handleSendMessage}
                          disabled={
                            (!inputValue.trim() &&
                              attachedFiles.length === 0) ||
                            loading
                          }
                        >
                          <Send size={20} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>

                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileAttachment}
                    className="d-none"
                    accept="image/*"
                  />

                  {/* <div className="text-center mt-3">
                  <small className="help-text text-muted">
                    Press Enter to send, Shift + Enter for new line
                  </small>
                </div> */}
                </Col>
              </Row>
            </Container>
          </div>
        </Container>
      </div>
    </>
  );
}

export default ChatApp;
